import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { 
  ClipboardCheck, 
  Stethoscope, 
  Activity, 
  HeartPulse,
  Shield,
  UserCheck,
  Sparkles,
  Clock,
  ChevronRight,
  Check,
  Phone
} from "lucide-react";

const journeySteps = [
  {
    icon: ClipboardCheck,
    title: "Pre-Operative Consultation",
    description: "Meet with our anesthesiologist before your surgery day. We'll review your medical history, discuss anesthesia options, and answer all your questions.",
    details: ["Medical history review", "Physical assessment", "Anesthesia plan discussion"],
  },
  {
    icon: Stethoscope,
    title: "Day of Surgery Preparation",
    description: "Arrive at our comfortable facility where our team will prepare you for your procedure. We ensure you're relaxed and fully informed.",
    details: ["Comfortable waiting area", "IV placement", "Final safety checks"],
  },
  {
    icon: Activity,
    title: "During Your Procedure",
    description: "Our board-certified anesthesiologist monitors you continuously throughout surgery, adjusting care as needed for optimal safety and comfort.",
    details: ["Continuous monitoring", "Vital sign management", "Pain control"],
  },
  {
    icon: HeartPulse,
    title: "Recovery & Discharge",
    description: "Wake up in our dedicated recovery area with attentive nursing care. We ensure you're comfortable before discharge with clear follow-up instructions.",
    details: ["Post-anesthesia care", "Pain management", "Discharge instructions"],
  },
];

const benefits = [
  { icon: Shield, name: "Board-Certified Physicians", desc: "Expert anesthesiologists on every case" },
  { icon: Activity, name: "Advanced Monitoring", desc: "State-of-the-art equipment and technology" },
  { icon: UserCheck, name: "Personalized Care", desc: "Anesthesia plans tailored to your needs" },
  { icon: Clock, name: "Efficient Service", desc: "Minimal wait times and streamlined care" },
  { icon: HeartPulse, name: "Pain Management", desc: "Comprehensive post-operative pain control" },
  { icon: Phone, name: "24/7 Support", desc: "Available for post-procedure questions" },
];

export default function VIPExperience() {
  return (
    <div data-testid="vip-page" className="min-h-screen pt-24">
      {/* Hero Section */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-30"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1551190822-a9333d879b1f?w=1920&h=1080&fit=crop')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-vip-black via-vip-black/90 to-vip-black" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="text-neon-pink uppercase tracking-[0.3em] text-sm mb-4">Patient Experience</p>
          <h1 className="font-heading text-4xl sm:text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-none font-bold italic text-white mb-8">
            Your <span className="gradient-text">Surgical</span>
            <br />
            Journey
          </h1>
          <p className="text-gray-300 text-base md:text-lg lg:text-xl max-w-2xl mx-auto">
            From consultation to recovery, we guide you through every step 
            of your anesthesia experience with expert care and attention.
          </p>
        </div>
      </section>

      {/* Journey Timeline */}
      <section data-testid="journey-section" className="py-24 md:py-32 bg-vip-paper">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-20">
            <p className="text-neon-cyan uppercase tracking-[0.2em] text-sm mb-4">The Process</p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl tracking-tight font-medium text-white">
              What to Expect
            </h2>
          </div>

          <div className="relative">
            {/* Vertical line connector (desktop) */}
            <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-neon-cyan via-royal-purple to-neon-pink" />

            <div className="space-y-12 lg:space-y-0">
              {journeySteps.map((step, index) => (
                <div
                  key={step.title}
                  data-testid={`journey-step-${index}`}
                  className={`lg:flex items-center gap-8 ${
                    index % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse"
                  }`}
                >
                  {/* Content */}
                  <div className={`lg:w-1/2 ${index % 2 === 0 ? "lg:text-right lg:pr-16" : "lg:pl-16"}`}>
                    <div className={`vip-card glass rounded-2xl p-8 border border-white/10 hover:border-neon-cyan/30 ${
                      index % 2 === 0 ? "lg:ml-auto" : ""
                    } max-w-md`}>
                      <div className={`flex items-center gap-4 mb-4 ${index % 2 === 0 ? "lg:justify-end" : ""}`}>
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-neon-cyan/20 to-neon-pink/20 flex items-center justify-center">
                          <step.icon className="w-7 h-7 text-neon-cyan" />
                        </div>
                        <h3 className="font-heading text-xl md:text-2xl text-white">{step.title}</h3>
                      </div>
                      <p className="text-gray-400 mb-4">{step.description}</p>
                      <ul className={`space-y-2 ${index % 2 === 0 ? "lg:text-right" : ""}`}>
                        {step.details.map((detail, idx) => (
                          <li key={idx} className={`flex items-center gap-2 text-gray-300 text-sm ${
                            index % 2 === 0 ? "lg:flex-row-reverse" : ""
                          }`}>
                            <Check className="w-4 h-4 text-neon-pink" />
                            {detail}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Center dot (desktop) */}
                  <div className="hidden lg:flex items-center justify-center">
                    <div className="relative">
                      <div className="w-6 h-6 rounded-full bg-vip-paper border-4 border-neon-cyan" />
                      <div className="absolute inset-0 rounded-full bg-neon-cyan/30 animate-ping" />
                    </div>
                  </div>

                  {/* Spacer for alternating layout */}
                  <div className="hidden lg:block lg:w-1/2" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section data-testid="benefits-section" className="py-24 md:py-32 bg-vip-black">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-neon-pink uppercase tracking-[0.2em] text-sm mb-4">Why Choose Us</p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl tracking-tight font-medium text-white">
              Patient Benefits
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <div
                key={benefit.name}
                data-testid={`benefit-${index}`}
                className="vip-card glass rounded-2xl p-6 border border-white/10 hover:border-neon-pink/30 flex items-start gap-4"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-neon-pink/20 to-royal-purple/20 flex items-center justify-center flex-shrink-0">
                  <benefit.icon className="w-6 h-6 text-neon-pink" />
                </div>
                <div>
                  <h3 className="font-heading text-lg text-white mb-1">{benefit.name}</h3>
                  <p className="text-gray-400 text-sm">{benefit.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pre-Op Instructions Section */}
      <section className="py-24 md:py-32 bg-vip-paper">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="relative">
                <div className="aspect-[4/5] rounded-2xl overflow-hidden border border-white/10">
                  <img
                    src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=600&h=750&fit=crop"
                    alt="Doctor consulting with patient"
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Decorative glow */}
                <div className="absolute -inset-4 bg-gradient-to-r from-neon-cyan/20 via-transparent to-neon-pink/20 rounded-3xl -z-10 blur-xl" />
              </div>
            </div>
            
            <div className="order-1 lg:order-2">
              <p className="text-neon-cyan uppercase tracking-[0.2em] text-sm mb-4">Before Surgery</p>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl tracking-tight font-medium text-white mb-8">
                Pre-Operative
                <br />
                <span className="gradient-text">Instructions</span>
              </h2>
              <p className="text-gray-300 text-base md:text-lg leading-relaxed mb-6">
                Proper preparation is essential for a safe anesthesia experience. 
                Our team will provide you with specific instructions based on your procedure.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="glass rounded-lg p-4 border border-white/10">
                  <h4 className="text-neon-cyan font-medium mb-2">Fasting Guidelines</h4>
                  <p className="text-gray-400 text-sm">No food or drink after midnight before your surgery (specific instructions provided)</p>
                </div>
                <div className="glass rounded-lg p-4 border border-white/10">
                  <h4 className="text-neon-pink font-medium mb-2">Medications</h4>
                  <p className="text-gray-400 text-sm">Bring a list of all medications, including supplements and over-the-counter drugs</p>
                </div>
                <div className="glass rounded-lg p-4 border border-white/10">
                  <h4 className="text-royal-purple font-medium mb-2">Transportation</h4>
                  <p className="text-gray-400 text-sm">Arrange for a responsible adult to drive you home after your procedure</p>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-4">
                <Link to="/appointments">
                  <Button
                    data-testid="vip-book-btn"
                    className="rounded-full bg-white text-black hover:bg-neon-cyan hover:text-black font-bold uppercase tracking-widest px-8 py-4 shadow-[0_0_20px_rgba(255,255,255,0.3)] hover:shadow-[0_0_30px_rgba(0,240,255,0.6)] transition-all duration-300"
                  >
                    Schedule Consultation
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button
                    data-testid="vip-contact-btn"
                    variant="outline"
                    className="rounded-full border border-white/20 bg-transparent text-white hover:border-neon-pink hover:text-neon-pink uppercase tracking-widest px-8 py-4 transition-all duration-300"
                  >
                    Contact Us
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Highlight */}
      <section className="py-24 md:py-32 bg-vip-black relative overflow-hidden">
        <div 
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse at center, rgba(191, 64, 191, 0.1) 0%, transparent 60%)'
          }}
        />
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="mb-8">
            <span className="text-5xl md:text-6xl font-heading text-royal-purple/50">"</span>
          </div>
          <blockquote className="font-heading text-xl sm:text-2xl md:text-3xl lg:text-4xl text-white italic leading-relaxed mb-8">
            The anesthesia team made me feel completely at ease before my surgery. 
            They explained everything clearly and I woke up comfortable with no nausea.
          </blockquote>
          <p className="text-neon-cyan uppercase tracking-widest text-sm">
            — Verified Patient
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-vip-paper">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl tracking-tight font-bold italic text-white mb-6">
            Questions About Your <span className="gradient-text">Procedure</span>?
          </h2>
          <p className="text-gray-300 text-base md:text-lg mb-10">
            Our anesthesia team is here to answer all your questions and ensure your comfort.
          </p>
          <Link to="/appointments">
            <Button
              data-testid="vip-cta-btn"
              className="shimmer-btn rounded-full bg-gradient-to-r from-neon-cyan via-royal-purple to-neon-pink text-white font-bold uppercase tracking-widest px-10 md:px-12 py-5 md:py-6 text-sm md:text-base shadow-[0_0_40px_rgba(191,64,191,0.4)] hover:shadow-[0_0_60px_rgba(191,64,191,0.6)] transition-all duration-300"
            >
              Schedule Consultation
              <Sparkles className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
